import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import '../styles/homeV2.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const timeRanges = ["hour", "day", "3days", "week", "month", "half-year", "year"];

const HomeV2 = ({ onSwitchV1, darkMode, setDarkMode }) => {
  // --- Стани для графіків ---
  const [statsBlocks, setStatsBlocks] = useState({ labels: [], datasets: [] });
  const [statsBlobs, setStatsBlobs] = useState({ labels: [], datasets: [] });
  const [statsAI, setStatsAI] = useState({ labels: [], datasets: [] });
  const [statsBlobsPerBlock, setStatsBlobsPerBlock] = useState({ labels: [], datasets: [] });
  const [statsMaxMinBlobSize, setStatsMaxMinBlobSize] = useState({ labels: [], datasets: [] });
  const [statsRollup, setStatsRollup] = useState({ labels: [], datasets: [] });
  const [statsBlobsByType, setStatsBlobsByType] = useState({ labels: [], datasets: [] });

  const [timeRangeBlocks, setTimeRangeBlocks] = useState("hour");
  const [timeRangeBlobs, setTimeRangeBlobs] = useState("hour");
  const [timeRangeAI, setTimeRangeAI] = useState("hour");
  const [timeRangeBlobsPerBlock, setTimeRangeBlobsPerBlock] = useState("hour");
  const [timeRangeMaxMinBlobSize, setTimeRangeMaxMinBlobSize] = useState("hour");
  const [timeRangeRollup, setTimeRangeRollup] = useState("hour");
  const [timeRangeBlobsByType, setTimeRangeBlobsByType] = useState("hour");

  const [loadingBlocks, setLoadingBlocks] = useState(true);
  const [loadingBlobs, setLoadingBlobs] = useState(true);
  const [loadingAI, setLoadingAI] = useState(true);
  const [loadingBlobsPerBlock, setLoadingBlobsPerBlock] = useState(true);
  const [loadingMaxMinBlobSize, setLoadingMaxMinBlobSize] = useState(true);
  const [loadingRollup, setLoadingRollup] = useState(true);
  const [loadingBlobsByType, setLoadingBlobsByType] = useState(true);

  // --- Завантаження Total Blocks ---
  useEffect(() => {
    fetchChartData(timeRangeBlocks, "total_blocks", setStatsBlocks, setLoadingBlocks);
  }, [timeRangeBlocks]);

  // --- Завантаження Total Blobs ---
  useEffect(() => {
    fetchChartData(timeRangeBlobs, "total_blobs", setStatsBlobs, setLoadingBlobs);
  }, [timeRangeBlobs]);

  // --- Завантаження Blobs AI Analytics ---
  useEffect(() => {
    fetchAIChartData(timeRangeAI, setStatsAI, setLoadingAI);
  }, [timeRangeAI]);


  // --- Завантаження Blobs per Block ---
  useEffect(() => {
    fetchBlobsPerBlockChartData(timeRangeBlobsPerBlock, setStatsBlobsPerBlock, setLoadingBlobsPerBlock);
  }, [timeRangeBlobsPerBlock]);

  // --- Завантаження Max/Min Blob Size ---
  useEffect(() => {
    fetchMaxMinBlobSizeChartData(timeRangeMaxMinBlobSize, setStatsMaxMinBlobSize, setLoadingMaxMinBlobSize);
  }, [timeRangeMaxMinBlobSize]);

  // --- Завантаження Rollup ---
  useEffect(() => {
    fetchRollupChartData(timeRangeRollup, setStatsRollup, setLoadingRollup);
  }, [timeRangeRollup]);

  // --- Завантаження Blobs по типу ---
  useEffect(() => {
    fetchBlobsByTypeChartData(timeRangeBlobsByType, setStatsBlobsByType, setLoadingBlobsByType);
  }, [timeRangeBlobsByType]);

  // --- Функція для Total Blocks/Blobs ---
  async function fetchChartData(timeRange, mainField, setStats, setLoading) {
    setLoading(true);
    try {
      let url = "https://da-dashboard-api.r1m.team/static/stats/";
      let labels = [];
      let dataPoints = [];

      switch (timeRange) {
        case "hour": {
          // 1. Визначаємо 24 останні години (UTC)
          const now = new Date();
          let hoursArr = [];
          for (let i = 23; i >= 0; i--) {
            const d = new Date(now.getTime() - i * 60 * 60 * 1000);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            const hourStr = String(d.getUTCHours()).padStart(2, "0");
            hoursArr.push({ day: dayStr, hour: hourStr, label: `${hourStr}:00` });
          }
          // 2. Завантажуємо дані для кожної години
          const hourData = await Promise.all(
            hoursArr.map(({ day, hour }) =>
              fetch(`${url}hour/${day}_${hour}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = hoursArr.map(h => h.label);
          dataPoints = hourData.map(d => d[mainField] ?? 0); // <-- Ось правильний рядок
          break;
        }
        case "day": {
          const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            return date.toISOString().split('T')[0].replace(/-/g, '');
          }).reverse();
          const dayData = await Promise.all(
            days.map(day =>
              fetch(`${url}day/${day}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = days.map(day => {
            const date = new Date(`${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6)}`);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          dataPoints = dayData.map(d => d[mainField] ?? 0);
          break;
        }
        case "3days": {
          // 1. Генеруємо всі дати за останні 60 днів (можна змінити діапазон)
          const possibleDays = [];
          const today = new Date();
          for (let i = 0; i < 60; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            possibleDays.push(dayStr);
          }
          // 2. Пробуємо завантажити файл для кожної дати
          const dayData = await Promise.all(
            possibleDays.map(day =>
              fetch(`${url}3days/${day}.json`).then(r => r.ok ? r.json() : null)
            )
          );
          // 3. Фільтруємо тільки ті, де є дані
          let filtered = possibleDays
            .map((day, i) => ({ day, data: dayData[i] }))
            .filter(item => item.data && Object.keys(item.data).length > 0);

          // 4. Сортуємо по даті у зростаючому порядку (старіші зліва, новіші справа)
          filtered = filtered.sort((a, b) => a.day.localeCompare(b.day));

          // 5. Беремо тільки останні 12 (або скільки треба)
          const lastFiltered = filtered.slice(-12);

          labels = lastFiltered.map(item => {
            const date = new Date(`${item.day.slice(0, 4)}-${item.day.slice(4, 6)}-${item.day.slice(6)}`);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          dataPoints = lastFiltered.map(item => item.data[mainField] ?? 0);
          break;
        }
        case "week": {
          // 9 останніх тижнів (від старого до нового)
          const now = new Date();
          let weeks = [];
          let year = now.getUTCFullYear();
          let week = Math.ceil(
            ((Date.UTC(year, now.getUTCMonth(), now.getUTCDate()) -
              Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
          );
          for (let i = 0; i < 9; i++) {
            weeks.unshift({ year, week });
            week--;
            if (week === 0) {
              year--;
              const lastWeek = Math.ceil(
                ((Date.UTC(year, 11, 31) - Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
              );
              week = lastWeek;
            }
          }
          const weekFiles = weeks.map(w => `${w.year}${String(w.week).padStart(2, "0")}`);
          const weekData = await Promise.all(
            weekFiles.map(file =>
              fetch(`${url}week/${file}.json`).then(r => r.ok ? r.json() : null)
            )
          );
          // Фільтруємо тільки ті, де є дані
          const filtered = weeks
            .map((w, i) => ({ w, data: weekData[i] }))
            .filter(item => item.data && Object.keys(item.data).length > 0);

          labels = filtered.map(item => `W${item.w.week} '${String(item.w.year).slice(2)}`);
          dataPoints = filtered.map(item => item.data[mainField] ?? 0);
          break;
        }
        case "month": {
          // 12 останніх місяців (від старого до нового)
          const now = new Date();
          let months = [];
          let year = now.getUTCFullYear();
          let month = now.getUTCMonth() + 1; // 1-12
          for (let i = 0; i < 12; i++) {
            months.unshift({ year, month });
            month--;
            if (month === 0) {
              month = 12;
              year--;
            }
          }
          const monthFiles = months.map(m => `${m.year}${String(m.month).padStart(2, "0")}`);
          const monthData = await Promise.all(
            monthFiles.map(file =>
              fetch(`${url}month/${file}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = months.map(m => {
            const d = new Date(Date.UTC(m.year, m.month - 1, 1));
            return d.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
          });
          dataPoints = monthData.map(d => d[mainField] ?? 0);
          break;
        }
        case "half-year": {
          const now = new Date();
          let halfyears = [];
          let year = now.getUTCFullYear();
          let half = now.getUTCMonth() < 6 ? 1 : 2;
          for (let i = 0; i < 6; i++) {
            halfyears.unshift({ year, half });
            half--;
            if (half === 0) {
              half = 2;
              year--;
            }
          }
          const halfyearFiles = halfyears.map(hy => `${hy.year}_H${hy.half}`);
          const halfyearData = await Promise.all(
            halfyearFiles.map(file =>
              fetch(`${url}halfyear/${file}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = halfyears.map(hy => `${hy.year} H${hy.half}`);
          dataPoints = halfyearData.map(d => d[mainField] ?? 0);
          break;
        }
        case "year": {
          const years = Array.from({ length: 2 }, (_, i) => {
            const date = new Date();
            date.setFullYear(date.getFullYear() - i);
            return date.getFullYear().toString();
          }).reverse();
          const yearData = await Promise.all(
            years.map(year =>
              fetch(`${url}year/${year}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = years;
          dataPoints = yearData.map(d => d[mainField] ?? 0);
          break;
        }
        default: {
          setStats({ labels: [], datasets: [] });
          setLoading(false);
          return;
        }
      }

      setStats({
        labels,
        datasets: [
          {
            label: mainField === "total_blocks" ? "Total Blocks" : "Total Blobs",
            data: dataPoints,
            backgroundColor: "#8e2fff",
            borderRadius: 6,
            barPercentage: 0.8,
            categoryPercentage: 0.8,
            borderSkipped: false
          }
        ]
      });
    } catch (error) {
      setStats({ labels: [], datasets: [] });
    } finally {
      setLoading(false);
    }
  }

  // --- Функція для Blobs AI Analytics ---
  async function fetchAIChartData(timeRange, setStats, setLoading) {
    setLoading(true);
    try {
      let url = "https://da-dashboard-api.r1m.team/static/stats/";
      let labels = [];
      let blobTypeStatsArr = [];

      switch (timeRange) {
        case "hour": {
          const now = new Date();
          let hoursArr = [];
          for (let i = 23; i >= 0; i--) {
            const d = new Date(now.getTime() - i * 60 * 60 * 1000);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            const hourStr = String(d.getUTCHours()).padStart(2, "0");
            hoursArr.push({ day: dayStr, hour: hourStr, label: `${hourStr}:00` });
          }
          const hourData = await Promise.all(
            hoursArr.map(({ day, hour }) =>
              fetch(`${url}hour/${day}_${hour}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = hoursArr.map(h => h.label);
          blobTypeStatsArr = hourData.map(d => d.blob_type_stats || {});
          break;
        }
        case "day": {
          const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            return date.toISOString().split('T')[0].replace(/-/g, '');
          }).reverse();
          const dayData = await Promise.all(
            days.map(day =>
              fetch(`${url}day/${day}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = days.map(day => {
            const date = new Date(`${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6)}`);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          blobTypeStatsArr = dayData.map(d => d.blob_type_stats || {});
          break;
        }
        case "3days": {
          // 1. Генеруємо всі дати за останні 60 днів (можна змінити діапазон)
          const possibleDays = [];
          const today = new Date();
          for (let i = 0; i < 60; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            possibleDays.push(dayStr);
          }
          // 2. Пробуємо завантажити файл для кожної дати
          const dayData = await Promise.all(
            possibleDays.map(day =>
              fetch(`${url}3days/${day}.json`).then(r => r.ok ? r.json() : null)
            )
          );
          // 3. Фільтруємо тільки ті, де є дані
          let filtered = possibleDays
            .map((day, i) => ({ day, data: dayData[i] }))
            .filter(item => item.data && Object.keys(item.data).length > 0);

          // 4. Сортуємо по даті у зростаючому порядку (старіші зліва, новіші справа)
          filtered = filtered.sort((a, b) => a.day.localeCompare(b.day));

          // 5. Беремо тільки останні 12 (або скільки треба)
          const lastFiltered = filtered.slice(-12);

          labels = lastFiltered.map(item => {
            const date = new Date(`${item.day.slice(0, 4)}-${item.day.slice(4, 6)}-${item.day.slice(6)}`);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          blobTypeStatsArr = lastFiltered.map(item => item.data.blob_type_stats || {});
          break;
        }
        case "week": {
          const now = new Date();
          let weeks = [];
          let year = now.getUTCFullYear();
          let week = Math.ceil(
            ((Date.UTC(year, now.getUTCMonth(), now.getUTCDate()) -
              Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
          );
          for (let i = 0; i < 9; i++) {
            weeks.unshift({ year, week });
            week--;
            if (week === 0) {
              year--;
              const lastWeek = Math.ceil(
                ((Date.UTC(year, 11, 31) - Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
              );
              week = lastWeek;
            }
          }
          const weekFiles = weeks.map(w => `${w.year}${String(w.week).padStart(2, "0")}`);
          const weekData = await Promise.all(
            weekFiles.map(file =>
              fetch(`${url}week/${file}.json`).then(r => r.ok ? r.json() : null)
            )
          );
          // Фільтруємо тільки ті, де є дані
          const filtered = weeks
            .map((w, i) => ({ w, data: weekData[i] }))
            .filter(item => item.data && Object.keys(item.data).length > 0);

          labels = filtered.map(item => `W${item.w.week} '${String(item.w.year).slice(2)}`);
          blobTypeStatsArr = filtered.map(item => item.data.blob_type_stats || {});
          break;
        }
        case "month": {
          const now = new Date();
          let months = [];
          let year = now.getUTCFullYear();
          let month = now.getUTCMonth() + 1;
          for (let i = 0; i < 12; i++) {
            months.unshift({ year, month });
            month--;
            if (month === 0) {
              month = 12;
              year--;
            }
          }
          const monthFiles = months.map(m => `${m.year}${String(m.month).padStart(2, "0")}`);
          const monthData = await Promise.all(
            monthFiles.map(file =>
              fetch(`${url}month/${file}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = months.map(m => {
            const d = new Date(Date.UTC(m.year, m.month - 1, 1));
            return d.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
          });
          blobTypeStatsArr = monthData.map(d => d.blob_type_stats || {});
          break;
        }
        case "half-year": {
          const now = new Date();
          let halfyears = [];
          let year = now.getUTCFullYear();
          let half = now.getUTCMonth() < 6 ? 1 : 2;
          for (let i = 0; i < 6; i++) {
            halfyears.unshift({ year, half });
            half--;
            if (half === 0) {
              half = 2;
              year--;
            }
          }
          const halfyearFiles = halfyears.map(hy => `${hy.year}_H${hy.half}`);
          const halfyearData = await Promise.all(
            halfyearFiles.map(file =>
              fetch(`${url}halfyear/${file}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = halfyears.map(hy => `${hy.year} H${hy.half}`);
          blobTypeStatsArr = halfyearData.map(d => d.blob_type_stats || {});
          break;
        }
        case "year": {
          const years = Array.from({ length: 2 }, (_, i) => {
            const date = new Date();
            date.setFullYear(date.getFullYear() - i);
            return date.getFullYear().toString();
          }).reverse();
          const yearData = await Promise.all(
            years.map(year =>
              fetch(`${url}year/${year}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = years;
          blobTypeStatsArr = yearData.map(d => d.blob_type_stats || {});
          break;
        }
        default: {
          setStats({ labels: [], datasets: [] });
          setLoading(false);
          return;
        }
      }

      // Визначаємо всі типи блобів
      const allTypes = Array.from(
        new Set(blobTypeStatsArr.flatMap(stats => Object.keys(stats)))
      );
      // Формуємо дані для кожного типу
      const blobTypeColors = {
        "TX Blob": "#8e2fff",
        "NFT Blob": "#ff4ecd",
        "Zk Rollup TX": "#c299fc",
        "AI Spam": "#f3bfff",
        "Test/Debug Blob": "#e9d7fd",
        "Other/Unclassified": "#f8f0ff",
        "Other / Unclassified": "#f8f0ff"
      };

      const datasets = allTypes.map((type) => ({
        label: type,
        data: blobTypeStatsArr.map(stats => stats[type]?.count ?? 0),
        backgroundColor: blobTypeColors[type] || "#f8f0ff",
        borderRadius: 0,
        barPercentage: 0.8,
        categoryPercentage: 0.8,
        borderSkipped: false
      }));

      setStats({
        labels,
        datasets,
        rawBlobTypeStatsArr: blobTypeStatsArr // <-- Додаємо цей масив для тултіпу
      });
    } catch (error) {
      setStats({ labels: [], datasets: [] });
    } finally {
      setLoading(false);
    }
  }

  // --- Функція для Blobs per Block ---
  async function fetchBlobsPerBlockChartData(timeRange, setStats, setLoading) {
    setLoading(true);
    try {
      let url = "https://da-dashboard-api.r1m.team/static/stats/";
      let labels = [];
      let maxArr = [];
      let minArr = [];
      let avgArr = [];

      switch (timeRange) {
        case "hour": {
          const now = new Date();
          let hoursArr = [];
          for (let i = 23; i >= 0; i--) {
            const d = new Date(now.getTime() - i * 60 * 60 * 1000);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            const hourStr = String(d.getUTCHours()).padStart(2, "0");
            hoursArr.push({ day: dayStr, hour: hourStr, label: `${hourStr}:00` });
          }
          const hourData = await Promise.all(
            hoursArr.map(({ day, hour }) =>
              fetch(`${url}hour/${day}_${hour}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = hoursArr.map(h => h.label);
          maxArr = hourData.map(d => d.blobs_per_block_stats?.max ?? 0);
          minArr = hourData.map(d => d.blobs_per_block_stats?.min ?? 0);
          avgArr = hourData.map(d => d.blobs_per_block_stats?.avg ?? 0);
          break;
        }
        case "day": {
          const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            return date.toISOString().split('T')[0].replace(/-/g, '');
          }).reverse();
          const dayData = await Promise.all(
            days.map(day =>
              fetch(`${url}day/${day}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = days.map(day => {
            const date = new Date(`${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6)}`);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          maxArr = dayData.map(d => d.blobs_per_block_stats?.max ?? 0);
          minArr = dayData.map(d => d.blobs_per_block_stats?.min ?? 0);
          avgArr = dayData.map(d => d.blobs_per_block_stats?.avg ?? 0);
          break;
        }
        case "3days": {
          const possibleDays = [];
          const today = new Date();
          for (let i = 0; i < 60; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            possibleDays.push(dayStr);
          }
          const dayData = await Promise.all(
            possibleDays.map(day =>
              fetch(`${url}3days/${day}.json`).then(r => r.ok ? r.json() : null)
            )
          );
          let filtered = possibleDays
            .map((day, i) => ({ day, data: dayData[i] }))
            .filter(item => item.data && Object.keys(item.data).length > 0);

          filtered = filtered.sort((a, b) => a.day.localeCompare(b.day));
          const lastFiltered = filtered.slice(-12);

          labels = lastFiltered.map(item => {
            const date = new Date(`${item.day.slice(0, 4)}-${item.day.slice(4, 6)}-${item.day.slice(6)}`);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          maxArr = lastFiltered.map(item => item.data.blobs_per_block_stats?.max ?? 0);
          minArr = lastFiltered.map(item => item.data.blobs_per_block_stats?.min ?? 0);
          avgArr = lastFiltered.map(item => item.data.blobs_per_block_stats?.avg ?? 0);
          break;
        }
        case "week": {
          const now = new Date();
          let weeks = [];
          let year = now.getUTCFullYear();
          let week = Math.ceil(
            ((Date.UTC(year, now.getUTCMonth(), now.getUTCDate()) -
              Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
          );
          for (let i = 0; i < 9; i++) {
            weeks.unshift({ year, week });
            week--;
            if (week === 0) {
              year--;
              const lastWeek = Math.ceil(
                ((Date.UTC(year, 11, 31) - Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
              );
              week = lastWeek;
            }
          }
          const weekFiles = weeks.map(w => `${w.year}${String(w.week).padStart(2, "0")}`);
          const weekData = await Promise.all(
            weekFiles.map(file =>
              fetch(`${url}week/${file}.json`).then(r => r.ok ? r.json() : null)
            )
          );
          const filtered = weeks
            .map((w, i) => ({ w, data: weekData[i] }))
            .filter(item => item.data && Object.keys(item.data).length > 0);

          labels = filtered.map(item => `W${item.w.week} '${String(item.w.year).slice(2)}`);
          maxArr = filtered.map(item => item.data.blobs_per_block_stats?.max ?? 0);
          minArr = filtered.map(item => item.data.blobs_per_block_stats?.min ?? 0);
          avgArr = filtered.map(item => item.data.blobs_per_block_stats?.avg ?? 0);
          break;
        }
        case "month": {
          const now = new Date();
          let months = [];
          let year = now.getUTCFullYear();
          let month = now.getUTCMonth() + 1;
          for (let i = 0; i < 12; i++) {
            months.unshift({ year, month });
            month--;
            if (month === 0) {
              month = 12;
              year--;
            }
          }
          const monthFiles = months.map(m => `${m.year}${String(m.month).padStart(2, "0")}`);
          const monthData = await Promise.all(
            monthFiles.map(file =>
              fetch(`${url}month/${file}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = months.map(m => {
            const d = new Date(Date.UTC(m.year, m.month - 1, 1));
            return d.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
          });
          maxArr = monthData.map(d => d.blobs_per_block_stats?.max ?? 0);
          minArr = monthData.map(d => d.blobs_per_block_stats?.min ?? 0);
          avgArr = monthData.map(d => d.blobs_per_block_stats?.avg ?? 0);
          break;
        }
        case "half-year": {
          const now = new Date();
          let halfyears = [];
          let year = now.getUTCFullYear();
          let half = now.getUTCMonth() < 6 ? 1 : 2;
          for (let i = 0; i < 6; i++) {
            halfyears.unshift({ year, half });
            half--;
            if (half === 0) {
              half = 2;
              year--;
            }
          }
          const halfyearFiles = halfyears.map(hy => `${hy.year}_H${hy.half}`);
          const halfyearData = await Promise.all(
            halfyearFiles.map(file =>
              fetch(`${url}halfyear/${file}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = halfyears.map(hy => `${hy.year} H${hy.half}`);
          maxArr = halfyearData.map(d => d.blobs_per_block_stats?.max ?? 0);
          minArr = halfyearData.map(d => d.blobs_per_block_stats?.min ?? 0);
          avgArr = halfyearData.map(d => d.blobs_per_block_stats?.avg ?? 0);
          break;
        }
        case "year": {
          const years = Array.from({ length: 2 }, (_, i) => {
            const date = new Date();
            date.setFullYear(date.getFullYear() - i);
            return date.getFullYear().toString();
          }).reverse();
          const yearData = await Promise.all(
            years.map(year =>
              fetch(`${url}year/${year}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = years;
          maxArr = yearData.map(d => d.blobs_per_block_stats?.max ?? 0);
          minArr = yearData.map(d => d.blobs_per_block_stats?.min ?? 0);
          avgArr = yearData.map(d => d.blobs_per_block_stats?.avg ?? 0);
          break;
        }
        default: {
          setStats({ labels: [], datasets: [] });
          setLoading(false);
          return;
        }
      }

      setStats({
        labels,
        datasets: [
          {
            label: "Max",
            data: maxArr,
            backgroundColor: "#8e2fff",
            borderRadius: 6,
            barPercentage: 0.8,
            categoryPercentage: 0.8,
            borderSkipped: false
          },
          {
            label: "Min",
            data: minArr,
            backgroundColor: "#b988fc",
            borderRadius: 6,
            barPercentage: 0.8,
            categoryPercentage: 0.8,
            borderSkipped: false
          }
        ],
        rawAvgArr: avgArr // <-- Додаємо avgArr для тултіпу!
      });
    } catch (error) {
      setStats({ labels: [], datasets: [] });
    } finally {
      setLoading(false);
    }
  }

  
  // --- Функція для Max/Min Blob Size ---
  async function fetchMaxMinBlobSizeChartData(timeRange, setStats, setLoading) {
    setLoading(true);
    try {
      let url = "https://da-dashboard-api.r1m.team/static/stats/";
      let labels = [];
      let maxArr = [];
      let minArr = [];
      let avgArr = [];

      switch (timeRange) {
        case "hour": {
          const now = new Date();
          let hoursArr = [];
          for (let i = 23; i >= 0; i--) {
            const d = new Date(now.getTime() - i * 60 * 60 * 1000);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            const hourStr = String(d.getUTCHours()).padStart(2, "0");
            hoursArr.push({ day: dayStr, hour: hourStr, label: `${hourStr}:00` });
          }
          const hourData = await Promise.all(
            hoursArr.map(({ day, hour }) =>
              fetch(`${url}hour/${day}_${hour}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = hoursArr.map(h => h.label);
          maxArr = hourData.map(d => d.max_blob_size ?? 0);
          minArr = hourData.map(d => d.min_blob_size ?? 0);
          break;
        }
        case "day": {
          const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            return date.toISOString().split('T')[0].replace(/-/g, '');
          }).reverse();
          const dayData = await Promise.all(
            days.map(day =>
              fetch(`${url}day/${day}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = days.map(day => {
            const date = new Date(`${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6)}`);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          maxArr = dayData.map(d => d.max_blob_size ?? 0);
          minArr = dayData.map(d => d.min_blob_size ?? 0);
          break;
        }
        case "3days": {
          const possibleDays = [];
          const today = new Date();
          for (let i = 0; i < 60; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            possibleDays.push(dayStr);
          }
          const dayData = await Promise.all(
            possibleDays.map(day =>
              fetch(`${url}3days/${day}.json`).then(r => r.ok ? r.json() : null)
            )
          );
          let filtered = possibleDays
            .map((day, i) => ({ day, data: dayData[i] }))
            .filter(item => item.data && Object.keys(item.data).length > 0);

          filtered = filtered.sort((a, b) => a.day.localeCompare(b.day));
          const lastFiltered = filtered.slice(-12);

          labels = lastFiltered.map(item => {
            const date = new Date(`${item.day.slice(0, 4)}-${item.day.slice(4, 6)}-${item.day.slice(6)}`);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          maxArr = lastFiltered.map(item => item.data.max_blob_size ?? 0);
          minArr = lastFiltered.map(item => item.data.min_blob_size ?? 0);
          break;
        }
        case "week": {
          const now = new Date();
          let weeks = [];
          let year = now.getUTCFullYear();
          let week = Math.ceil(
            ((Date.UTC(year, now.getUTCMonth(), now.getUTCDate()) -
              Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
          );
          for (let i = 0; i < 9; i++) {
            weeks.unshift({ year, week });
            week--;
            if (week === 0) {
              year--;
              const lastWeek = Math.ceil(
                ((Date.UTC(year, 11, 31) - Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
              );
              week = lastWeek;
            }
          }
          const weekFiles = weeks.map(w => `${w.year}${String(w.week).padStart(2, "0")}`);
          const weekData = await Promise.all(
            weekFiles.map(file =>
              fetch(`${url}week/${file}.json`).then(r => r.ok ? r.json() : null)
            )
          );
          const filtered = weeks
            .map((w, i) => ({ w, data: weekData[i] }))
            .filter(item => item.data && Object.keys(item.data).length > 0);

          labels = filtered.map(item => `W${item.w.week} '${String(item.w.year).slice(2)}`);
          maxArr = filtered.map(item => item.data.max_blob_size ?? 0);
          minArr = filtered.map(item => item.data.min_blob_size ?? 0);
          break;
        }
        case "month": {
          const now = new Date();
          let months = [];
          let year = now.getUTCFullYear();
          let month = now.getUTCMonth() + 1;
          for (let i = 0; i < 12; i++) {
            months.unshift({ year, month });
            month--;
            if (month === 0) {
              month = 12;
              year--;
            }
          }
          const monthFiles = months.map(m => `${m.year}${String(m.month).padStart(2, "0")}`);
          const monthData = await Promise.all(
            monthFiles.map(file =>
              fetch(`${url}month/${file}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = months.map(m => {
            const d = new Date(Date.UTC(m.year, m.month - 1, 1));
            return d.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
          });
          maxArr = monthData.map(d => d.max_blob_size ?? 0);
          minArr = monthData.map(d => d.min_blob_size ?? 0);
          break;
        }
        case "half-year": {
          const now = new Date();
          let halfyears = [];
          let year = now.getUTCFullYear();
          let half = now.getUTCMonth() < 6 ? 1 : 2;
          for (let i = 0; i < 6; i++) {
            halfyears.unshift({ year, half });
            half--;
            if (half === 0) {
              half = 2;
              year--;
            }
          }
          const halfyearFiles = halfyears.map(hy => `${hy.year}_H${hy.half}`);
          const halfyearData = await Promise.all(
            halfyearFiles.map(file =>
              fetch(`${url}halfyear/${file}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = halfyears.map(hy => `${hy.year} H${hy.half}`);
          maxArr = halfyearData.map(d => d.max_blob_size ?? 0);
          minArr = halfyearData.map(d => d.min_blob_size ?? 0);
          break;
        }
        case "year": {
          const years = Array.from({ length: 2 }, (_, i) => {
            const date = new Date();
            date.setFullYear(date.getFullYear() - i);
            return date.getFullYear().toString();
          }).reverse();
          const yearData = await Promise.all(
            years.map(year =>
              fetch(`${url}year/${year}.json`).then(r => r.ok ? r.json() : {})
            )
          );
          labels = years;
          maxArr = yearData.map(d => d.max_blob_size ?? 0);
          minArr = yearData.map(d => d.min_blob_size ?? 0);
          break;
        }
        default: {
          setStats({ labels: [], datasets: [] });
          setLoading(false);
          return;
        }
      }

      setStats({
        labels,
        datasets: [
          {
            label: "Max",
            data: maxArr,
            backgroundColor: "#8e2fff",
            borderRadius: 6,
            barPercentage: 0.8,
            categoryPercentage: 0.8,
            borderSkipped: false
          },
          {
            label: "Min",
            data: minArr,
            backgroundColor: "#b988fc",
            borderRadius: 6,
            barPercentage: 0.8,
            categoryPercentage: 0.8,
            borderSkipped: false
          }
        ],
        rawAvgArr: avgArr // <-- Додаємо avgArr для тултіпу!
      });
    } catch (error) {
      setStats({ labels: [], datasets: [] });
    } finally {
      setLoading(false);
    }
}

  // --- Функція для Rollup ---
  // Для кожного періоду використовуємо лише останній доступний запис
  async function fetchRollupChartData(timeRange, setStats, setLoading) {
    setLoading(true);
    try {
      let url = "https://da-dashboard-api.r1m.team/static/stats/";
      let lastStats = {};

      switch (timeRange) {
        case "hour": {
          const now = new Date();
          let found = false;
          for (let i = 0; i < 48 && !found; i++) {
            const d = new Date(now.getTime() - i * 60 * 60 * 1000);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            const hourStr = String(d.getUTCHours()).padStart(2, "0");
            try {
              const resp = await fetch(`${url}hour/${dayStr}_${hourStr}.json`);
              if (resp.ok) {
                const data = await resp.json();
                if (data.rollup_stats && Object.keys(data.rollup_stats).length > 0) {
                  lastStats = data.rollup_stats;
                  found = true;
                }
              }
            } catch {}
          }
          break;
        }
        case "day": {
          const today = new Date();
          let found = false;
          for (let i = 0; i < 30 && !found; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            try {
              const resp = await fetch(`${url}day/${dayStr}.json`);
              if (resp.ok) {
                const data = await resp.json();
                if (data.rollup_stats && Object.keys(data.rollup_stats).length > 0) {
                  lastStats = data.rollup_stats;
                  found = true;
                }
              }
            } catch {}
          }
          break;
        }
        case "3days": {
          const today = new Date();
          let found = false;
          for (let i = 0; i < 30 && !found; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
            try {
              const resp = await fetch(`${url}3days/${dayStr}.json`);
              if (resp.ok) {
                const data = await resp.json();
                if (data.rollup_stats && Object.keys(data.rollup_stats).length > 0) {
                  lastStats = data.rollup_stats;
                  found = true;
                }
              }
            } catch {}
          }
          break;
        }
        case "week": {
          const now = new Date();
          let year = now.getUTCFullYear();
          let week = Math.ceil(
            ((Date.UTC(year, now.getUTCMonth(), now.getUTCDate()) -
              Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
          );
          let found = false;
          for (let i = 0; i < 12 && !found; i++) {
            const file = `${year}${String(week).padStart(2, "0")}`;
            try {
              const resp = await fetch(`${url}week/${file}.json`);
              if (resp.ok) {
                const data = await resp.json();
                if (data.rollup_stats && Object.keys(data.rollup_stats).length > 0) {
                  lastStats = data.rollup_stats;
                  found = true;
                }
              }
            } catch {}
            week--;
            if (week === 0) {
              year--;
              const lastWeek = Math.ceil(
                ((Date.UTC(year, 11, 31) - Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
              );
              week = lastWeek;
            }
          }
          break;
        }
        case "month": {
          const now = new Date();
          let year = now.getUTCFullYear();
          let month = now.getUTCMonth() + 1;
          let found = false;
          for (let i = 0; i < 12 && !found; i++) {
            const file = `${year}${String(month).padStart(2, "0")}`;
            try {
              const resp = await fetch(`${url}month/${file}.json`);
              if (resp.ok) {
                const data = await resp.json();
                if (data.rollup_stats && Object.keys(data.rollup_stats).length > 0) {
                  lastStats = data.rollup_stats;
                  found = true;
                }
              }
            } catch {}
            month--;
            if (month === 0) {
              month = 12;
              year--;
            }
          }
          break;
        }
        case "half-year": {
          const now = new Date();
          let year = now.getUTCFullYear();
          let half = now.getUTCMonth() < 6 ? 1 : 2;
          let found = false;
          for (let i = 0; i < 6 && !found; i++) {
            const file = `${year}_H${half}`;
            try {
              const resp = await fetch(`${url}halfyear/${file}.json`);
              if (resp.ok) {
                const data = await resp.json();
                if (data.rollup_stats && Object.keys(data.rollup_stats).length > 0) {
                  lastStats = data.rollup_stats;
                  found = true;
                }
              }
            } catch {}
            half--;
            if (half === 0) {
              half = 2;
              year--;
            }
          }
          break;
        }
        case "year": {
          const now = new Date();
          let year = now.getUTCFullYear();
          let found = false;
          for (let i = 0; i < 3 && !found; i++) {
            const file = `${year}`;
            try {
              const resp = await fetch(`${url}year/${file}.json`);
              if (resp.ok) {
                const data = await resp.json();
                if (data.rollup_stats && Object.keys(data.rollup_stats).length > 0) {
                  lastStats = data.rollup_stats;
                  found = true;
                }
              }
            } catch {}
            year--;
          }
          break;
        }
        default: {
          setStats({ labels: [], datasets: [] });
          setLoading(false);
          return;
        }
      }

      // Якщо нічого не знайдено
      if (!lastStats || Object.keys(lastStats).length === 0) {
        setStats({ labels: [], datasets: [] });
        setLoading(false);
        return;
      }

      // labels — це rollup names
      const rollupNames = Object.keys(lastStats);
      const rollupCounts = rollupNames.map(name => lastStats[name]?.count ?? 0);
      const rollupPercents = rollupNames.map(name => lastStats[name]?.percent ?? 0);

      // --- Присвоюємо кожному rollup унікальний колір ---
      // (колір фіксується для кожного rollup на час сесії)
      rollupNames.forEach(name => getRollupColor(name));

      setStats({
        labels: rollupNames,
        datasets: [
          {
            label: "Rollup TX Count",
            data: rollupCounts,
            backgroundColor: rollupNames.map(name => rollupColorMap[name] || DEFAULT_ROLLUP_COLOR),
            borderRadius: 6,
            barPercentage: 0.8,
            categoryPercentage: 0.8,
            borderSkipped: false
          }
        ],
        rawRollupPercents: rollupPercents,
        rawRollupStats: lastStats // <-- Додаємо!
      });
    } catch (error) {
      setStats({ labels: [], datasets: [] });
    } finally {
      setLoading(false);
    }
  }

  // --- Заголовки ---
  const getChartTitle = (type, timeRange) => {
    let prefix =
      type === "blocks" ? "Total Blocks" :
      type === "blobs" ? "Total Blobs" :
      type === "ai" ? "Blobs AI Analytics" :
      type === "blobsPerBlock" ? "Blobs per Block: Max / Min" :
      type === "maxMinBlobSize" ? "Max / Min Blob Size" :
      "";
    switch (timeRange) {
      case "hour": return `${prefix} (Hour)`;
      case "day": return `${prefix} (Day)`;
      case "3days": return `${prefix} (3 Days)`;
      case "week": return `${prefix} (Week)`;
      case "month": return `${prefix} (Month)`;
      case "half-year": return `${prefix} (Half-year)`;
      case "year": return `${prefix} (Year)`;
      default: return prefix;
    }
  };

  // --- Рендер ---
  return (
    <div className={`dashboard-container${darkMode ? " dark" : ""}`}>
      <header className="dashboard-header">
  <div className="header-left">
    <div className={`theme-switch${darkMode ? ' active dark' : ''}`} onClick={() => setDarkMode(v => !v)}>
      <div className="switch-track"></div>
      <div className="switch-thumb">
        {darkMode ? (
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
            <path d="M15.5 13.5A7 7 0 0 1 6.5 4.5a7 7 0 1 0 9 9z" fill="#fff"/>
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="5" fill="#fff"/>
            <g stroke="#fff" strokeWidth="1.5">
              <line x1="10" y1="1" x2="10" y2="4"/>
              <line x1="10" y1="16" x2="10" y2="19"/>
              <line x1="1" y1="10" x2="4" y2="10"/>
              <line x1="16" y1="10" x2="19" y2="10"/>
              <line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/>
              <line x1="13.66" y1="13.66" x2="15.78" y2="15.78"/>
              <line x1="4.22" y1="15.78" x2="6.34" y2="13.66"/>
              <line x1="13.66" y1="6.34" x2="15.78" y2="4.22"/>
            </g>
          </svg>
        )}
      </div>
    </div>
  </div>
  <div className="header-center">
    <img
      src="/images/celestia_logo.png"
      alt="Logo"
      style={{
        height: 48,
        marginRight: 16,
        verticalAlign: "middle",
        display: "inline-block"
      }}
    />
    <h1 style={{ margin: 0, display: "inline-block", verticalAlign: "middle", fontWeight: 700 }}>
      AI-POWERED DA DASHBOARD
    </h1>
    <span className="version-label">
      v0.2.2-beta
    </span>
  </div>
  <div className="header-right">
    {/* Version switch toggle (V1↔V2) */}
    <div
      className={`theme-switch version-switch active`}
      onClick={onSwitchV1}
      title="Switch to V1"
      style={{ marginLeft: 16 }}
    >
      <div className="switch-track"></div>
      <div className="switch-thumb version-thumb">
        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>
          V1
        </span>
      </div>
    </div>
  </div>
</header>

      <div className="dashboard-content">
        <div className="dashboard-row">
          {/* Total Blocks */}
          <div className="dashboard-main-chart">
            <div className="chart-title">{getChartTitle("blocks", timeRangeBlocks)}</div>
            <div className="time-range-buttons">
              {timeRanges.map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRangeBlocks(range)}
                  className={`time-range-btn${timeRangeBlocks === range ? " active" : ""}`}
                >
                  {range === "3days" ? "3days" :
                   range === "half-year" ? "Half-year" :
                   range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
            {loadingBlocks ? (
              <div className="chart-loading">Loading data...</div>
            ) : (
              statsBlocks.labels.length > 0 && statsBlocks.datasets.length > 0 ? (
                <div className="chart-bar-container">
                  <Bar
                    data={statsBlocks}
                    options={{
                      plugins: {
                        legend: { display: true, position: "top" },
                        title: { display: false },
                      },
                      layout: { padding: 0 },
                      scales: {
                        x: {
                          title: { display: false },
                          grid: { display: false },
                          ticks: {
                            color: "#888",
                            font: { size: 11 },
                            maxRotation: 0,
                            minRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                          }
                        },
                        y: {
                          title: { display: false },
                          beginAtZero: true,
                          grid: { color: "#f0f0f0" },
                          ticks: { color: "#888" },
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      elements: {
                        bar: {
                          borderRadius: 6,
                          borderSkipped: false
                        }
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="chart-nodata">No data available</div>
              )
            )}
          </div>
          {/* Total Blobs */}
          <div className="dashboard-secondary-chart">
            <div className="chart-title">{getChartTitle("blobs", timeRangeBlobs)}</div>
            <div className="time-range-buttons">
              {timeRanges.map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRangeBlobs(range)}
                  className={`time-range-btn${timeRangeBlobs === range ? " active" : ""}`}
                >
                  {range === "3days" ? "3days" :
                   range === "half-year" ? "Half-year" :
                   range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
            {loadingBlobs ? (
              <div className="chart-loading">Loading data...</div>
            ) : (
              statsBlobs.labels.length > 0 && statsBlobs.datasets.length > 0 ? (
                <div className="chart-bar-container">
                  <Bar
                    data={statsBlobs}
                    options={{
                      plugins: {
                        legend: { display: true, position: "top" },
                        title: { display: false },
                      },
                      layout: { padding: 0 },
                      scales: {
                        x: {
                          title: { display: false },
                          grid: { display: false },
                          ticks: {
                            color: "#888",
                            font: { size: 11 },
                            maxRotation: 0,
                            minRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                          }
                        },
                        y: {
                          title: { display: false },
                          beginAtZero: true,
                          grid: { color: "#f0f0f0" },
                          ticks: { color: "#888" },
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      elements: {
                        bar: {
                          borderRadius: 6,
                          borderSkipped: false
                        }
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="chart-nodata">No data available</div>
              )
            )}
          </div>
        </div>
        <div className="dashboard-row">
          <div className="dashboard-main-chart">
            <div className="chart-title">Type Solo / Multi Blobs  ({timeRangeBlobsByType.charAt(0).toUpperCase() + timeRangeBlobsByType.slice(1)})</div>
            <div className="time-range-buttons">
              {timeRanges.map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRangeBlobsByType(range)}
                  className={`time-range-btn${timeRangeBlobsByType === range ? " active" : ""}`}
                >
                  {range === "3days" ? "3days" :
                    range === "half-year" ? "Half-year" :
                    range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
            {loadingBlobsByType ? (
              <div className="chart-loading">Loading data...</div>
            ) : (
              statsBlobsByType.labels.length > 0 && statsBlobsByType.datasets.length > 0 ? (
                <div className="chart-bar-container">
                  <Bar
                    data={statsBlobsByType}
                    options={{
                      plugins: {
                        legend: { display: true, position: "top" },
                        title: { display: false },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          callbacks: {
                            label: function(context) {
                              // label: "Solo" або "Multi"
                              const stats = statsBlobsByType?.rawAbTypeStatsArr?.[context.dataIndex] || {};
                              let typeStats;
                              if (context.dataset.label === "Solo") {
                                typeStats = stats.A || {};
                              } else if (context.dataset.label === "Multi") {
                                typeStats = stats.B || {};
                              } else {
                                typeStats = {};
                              }
                              const percent = typeStats.percent !== undefined ? `${typeStats.percent}%` : '-';
                              const count = typeStats.count ?? '-';
                              return `${context.dataset.label}: ${count} (${percent})`;
                            }
                          }
                        }
                      },
                      layout: { padding: 0 },
                      scales: {
                        x: {
                          stacked: true,
                          title: { display: false },
                          grid: { display: false },
                          ticks: {
                            color: "#888",
                            font: { size: 11 },
                            maxRotation: 0,
                            minRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                          }
                        },
                        y: {
                          stacked: true,
                          title: { display: false },
                          beginAtZero: true,
                          grid: { color: "#f0f0f0" },
                          ticks: { color: "#888" },
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      elements: {
                        bar: {
                          borderRadius: 6,
                          borderSkipped: false
                        }
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="chart-nodata">No data available</div>
              )
            )}
          </div>
        </div>
        {/* Blobs AI Analytics */}
        <div className="dashboard-row">
          <div className="dashboard-main-chart" style={{ width: "100%" }}>
            <div className="chart-title">{getChartTitle("ai", timeRangeAI)}</div>
            <div className="time-range-buttons">
              {timeRanges.map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRangeAI(range)}
                  className={`time-range-btn${timeRangeAI === range ? " active" : ""}`}
                >
                  {range === "3days" ? "3days" :
                   range === "half-year" ? "Half-year" :
                   range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
            {loadingAI ? (
              <div className="chart-loading">Loading data...</div>
            ) : (
              statsAI.labels.length > 0 && statsAI.datasets.length > 0 ? (
                <div className="chart-bar-container">
                  <Bar
                    data={statsAI}
                    options={{
                      plugins: {
                        legend: { display: true, position: "top" },
                        title: { display: false },
                        tooltip: {
  mode: 'index',
  intersect: false,
  callbacks: {
    label: function(context) {
      const label = context.dataset.label || '';
      const stats = statsAI?.rawBlobTypeStatsArr?.[context.dataIndex] || {};
      const typeStats = stats[label] || {};
      const percent = typeStats.percent !== undefined ? `${typeStats.percent}%` : '-';
      const count = typeStats.count ?? '-';
      const percentA = typeStats.percent_a !== undefined ? `${typeStats.percent_a}%` : '-';
      const percentB = typeStats.percent_b !== undefined ? `${typeStats.percent_b}%` : '-';
      const countA = typeStats.count_a ?? '-';
      const countB = typeStats.count_b ?? '-';
      return [
        `${label}: ${count} (${percent})`,
        `Solo ${countA} (${percentA}) | Multi ${countB} (${percentB})`
      ];
    },
    afterLabel: function() {
  return [' '];
}
  }
}
                      },
                      layout: { padding: 0 },
                      scales: {
                        x: {
                          stacked: true,
                          title: { display: false },
                          grid: { display: false },
                          ticks: {
                            color: "#888",
                            font: { size: 11 },
                            maxRotation: 0,
                            minRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                          }
                        },
                        y: {
                          stacked: true,
                          title: { display: false },
                          beginAtZero: true,
                          grid: { color: "#f0f0f0" },
                          ticks: { color: "#888" },
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      elements: {
                        bar: {
                          borderRadius: 6,
                          borderSkipped: false
                        }
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="chart-nodata">No data available</div>
              )
            )}
          </div>
        </div>
        <div className="dashboard-row">
          {/* Blobs per Block: Max / Min (дубль 1) */}
          <div className="dashboard-main-chart">
            <div className="chart-title">{getChartTitle("blobsPerBlock", timeRangeBlobsPerBlock)}</div>
            <div className="time-range-buttons">
              {timeRanges.map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRangeBlobsPerBlock(range)}
                  className={`time-range-btn${timeRangeBlobsPerBlock === range ? " active" : ""}`}
                >
                  {range === "3days" ? "3days" :
                    range === "half-year" ? "Half-year" :
                    range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
            {loadingBlobsPerBlock ? (
              <div className="chart-loading">Loading data...</div>
            ) : (
              statsBlobsPerBlock.labels.length > 0 && statsBlobsPerBlock.datasets.length > 0 ? (
                <div className="chart-bar-container">
                  <Bar
                    data={statsBlobsPerBlock}
                    options={{
                      plugins: {
                        legend: { display: true, position: "top" },
                        title: { display: false },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          callbacks: {
                            label: function(context) {
                              const idx = context.dataIndex;
                              if (context.datasetIndex === 0) {
                                return `Max: ${context.dataset.data[idx] ?? '-'}`;
                              }
                              if (context.datasetIndex === 1) {
                                return `Min: ${context.dataset.data[idx] ?? '-'}`;
                              }
                              return null;
                            },
                            afterBody: function(context) {
                              const idx = context[0]?.dataIndex ?? 0;
                              let avg = '-';
                              if (
                                statsBlobsPerBlock.rawAvgArr &&
                                Array.isArray(statsBlobsPerBlock.rawAvgArr) &&
                                typeof statsBlobsPerBlock.rawAvgArr[idx] !== 'undefined'
                              ) {
                                avg = statsBlobsPerBlock.rawAvgArr[idx];
                              }
                              return [`Avg: ${avg} %`];
                            }
                          }
                        }
                      },
                      layout: { padding: 0 },
                      scales: {
                        x: {
                          stacked: true,
                          title: { display: false },
                          grid: { display: false },
                          ticks: {
                            color: "#888",
                            font: { size: 11 },
                            maxRotation: 0,
                            minRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                          }
                        },
                        y: {
                          stacked: true,
                          title: { display: false },
                          beginAtZero: true,
                          grid: { color: "#f0f0f0" },
                          ticks: { color: "#888" },
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      elements: {
                        bar: {
                          borderRadius: 6,
                          borderSkipped: false
                        }
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="chart-nodata">No data available</div>
              )
            )}
          </div>
          {/* Max / Min Blob Size (дубль 1) */}
          <div className="dashboard-main-chart">
            <div className="chart-title">{getChartTitle("maxMinBlobSize", timeRangeMaxMinBlobSize)}</div>
            <div className="time-range-buttons">
              {timeRanges.map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRangeMaxMinBlobSize(range)}
                  className={`time-range-btn${timeRangeMaxMinBlobSize === range ? " active" : ""}`}
                >
                  {range === "3days" ? "3days" :
                   range === "half-year" ? "Half-year" :
                   range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
            {loadingMaxMinBlobSize ? (
              <div className="chart-loading">Loading data...</div>
            ) : (
              statsMaxMinBlobSize.labels.length > 0 && statsMaxMinBlobSize.datasets.length > 0 ? (
                <div className="chart-bar-container">
                  <Bar
                    data={statsMaxMinBlobSize}
                    options={{
                      plugins: {
                        legend: { display: true, position: "top" },
                        title: { display: false },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          callbacks: {
                            label: function(context) {
                              const label = context.dataset.label || '';
                              const value = context.parsed.y ?? '-';
                              return `${label}: ${value}`;
                            }
                          }
                        }
                      },
                      layout: { padding: 0 },
                      scales: {
                        x: {
                          stacked: true,
                          title: { display: false },
                          grid: { display: false },
                          ticks: {
                            color: "#888",
                            font: { size: 11 },
                            maxRotation: 0,
                            minRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                          }
                        },
                        y: {
                          stacked: true,
                          title: { display: false },
                          beginAtZero: true,
                          grid: { color: "#f0f0f0" },
                          ticks: { color: "#888" },
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      elements: {
                        bar: {
                          borderRadius: 6,
                          borderSkipped: false
                        }
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="chart-nodata">No data available</div>
              )
            )}
          </div>
        </div>
        <div className="dashboard-row">
          <div className="dashboard-main-chart rollup-analytics-chart" style={{ width: "100%" }}>
            <div className="chart-title">Rollup Analytics ({timeRangeRollup.charAt(0).toUpperCase() + timeRangeRollup.slice(1)})</div>
            <div className="time-range-buttons">
              {timeRanges.map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRangeRollup(range)}
                  className={`time-range-btn${timeRangeRollup === range ? " active" : ""}`}
                >
                  {range === "3days" ? "3days" :
                    range === "half-year" ? "Half-year" :
                    range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
            {loadingRollup ? (
              <div className="chart-loading">Loading data...</div>
            ) : (
              statsRollup.labels.length > 0 && statsRollup.datasets.length > 0 ? (
                <div className="chart-bar-container">
                  <Bar
                    data={statsRollup}
                    options={{
                      indexAxis: 'y',
                      plugins: {
                        legend: { display: false },
                        title: { display: false },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          callbacks: {
                            title: function(context) {
                              return context[0]?.label || '';
                            },
                            label: function(context) {
  const rollupName = context.label;
  const rollup = statsRollup.rawRollupStats?.[rollupName];
  if (!rollup) return null;

  // Основна інфа
  const lines = [
    `Total: ${rollup.count ?? '-'}` + (rollup.percent !== undefined ? ` (${rollup.percent}%)` : ''),
  ].filter(Boolean);

  // Типи (тільки якщо є хоча б одне ненульове значення)
  if (rollup.types && Object.keys(rollup.types).length > 0) {
    Object.entries(rollup.types).forEach(([type, values]) => {
      // Перевіряємо чи є хоча б одне ненульове значення
      const hasData = (values.A ?? 0) !== 0 || (values.B ?? 0) !== 0 || (values.Unknown ?? 0) !== 0 || (values.total ?? 0) !== 0;
      if (hasData) {
        lines.push(
          `   ${type}: Solo: ${values.A ?? 0} , Multi: ${values.B ?? 0}` +
          (values.total !== undefined ? ` , Total: ${values.total}` : '')
        );
      }
    });
  }

  return lines;
}
                          }
                        }
                      },
                      interaction: {
                        axis: 'y',      // <-- Додаємо це!
                        mode: 'nearest',
                        intersect: false
                      },
                      layout: { padding: 0 },
                      scales: {
                        x: {

                          title: { display: false },
                          grid: { display: true },
                          ticks: {
                            color: "#888",
                            font: { size: 11 },
                          }
                        },
                        y: {
                          title: { display: false },
                          grid: { display: false },
                          ticks: {
                            color: "#888",
                            font: { size: 11 },
                            autoSkip: false,
                          }
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      elements: {
                        bar: {
                          borderRadius: 6,
                          borderSkipped: false,
                          categoryPercentage: 0.7,
                          barPercentage: 0.8,
                        }
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="chart-nodata">No data available</div>
              )                                            
                       )}
         
          </div>
        </div>
      </div>
      <footer className="footer">
        <p>
          ©{" "}
          <a
            href="https://r1m.team"
            target="_blank"

            rel="noopener noreferrer"
            className="footer-link"
          >
            R1M
          </a>{" "}
          2025. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default HomeV2;

// --- Карта кольорів для rollup-ів (100 унікальних кольорів) ---
const ROLLUP_COLOR_PALETTE = [
  "#a259ff","#d6a4ff","#ff4ecd","#e0c3fc","#fbc2eb","#f8fafc","#7c3aed","#bdb2ff","#ff61a6","#e0e7ff",
  "#ffb6b9","#b5ead7","#ffdac1","#c7ceea","#f6eac2","#b5ead7","#c9c9ff","#f7b2b7","#f67280","#355c7d",
  "#6c5b7b","#c06c84","#f8b195","#f67280","#355c7d","#99b898","#feceab","#ff847c","#e84a5f","#2a363b",
  "#ffb347","#b5b9ff","#baffc9","#ffb3ba","#bae1ff","#b2f7ef","#f6abb6","#d6d4e0","#f7cac9","#92a8d1",
  "#f9d5e5","#b5ead7","#c7ceea","#ffb347","#b5b9ff","#baffc9","#ffb3ba","#bae1ff","#b2f7ef","#f6abb6",
  "#d6d4e0","#f7cac9","#92a8d1","#f9d5e5","#b5ead7","#c7ceea","#ffb347","#b5b9ff","#baffc9","#ffb3ba",
  "#bae1ff","#b2f7ef","#f6abb6","#d6d4e0","#f7cac9","#92a8d1","#f9d5e5","#b5ead7","#c7ceea","#ffb347",
  "#b5b9ff","#baffc9","#ffb3ba","#bae1ff","#b2f7ef","#f6abb6","#d6d4e0","#f7cac9","#92a8d1","#f9d5e5",
  "#b5ead7","#c7ceea","#ffb347","#b5b9ff","#baffc9","#ffb3ba","#bae1ff","#b2f7ef","#f6abb6","#d6d4e0",
  "#f7cac9","#92a8d1","#f9d5e5","#b5ead7","#c7ceea","#ffb347","#b5b9ff","#baffc9","#ffb3ba","#bae1ff"
];
const DEFAULT_ROLLUP_COLOR = "#cccccc";

// --- Динамічна карта rollup-колір (заповнюється автоматично) ---
let rollupColorMap = {};

function getRollupColor(name) {
  if (rollupColorMap[name]) return rollupColorMap[name];
  const usedColors = Object.values(rollupColorMap);
  const freeColor = ROLLUP_COLOR_PALETTE.find(c => !usedColors.includes(c));
  rollupColorMap[name] = freeColor || DEFAULT_ROLLUP_COLOR;
  return rollupColorMap[name];
}

// --- Функція для Blobs по типу ---
async function fetchBlobsByTypeChartData(timeRange, setStats, setLoading) {
  setLoading(true);
  try {
    let url = "https://da-dashboard-api.r1m.team/static/stats/";
    let labels = [];
    let abTypeStatsArr = [];

    switch (timeRange) {
      case "hour": {
        const now = new Date();
        let hoursArr = [];
        for (let i = 23; i >= 0; i--) {
          const d = new Date(now.getTime() - i * 60 * 60 * 1000);
          const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
          const hourStr = String(d.getUTCHours()).padStart(2, "0");
          hoursArr.push({ day: dayStr, hour: hourStr, label: `${hourStr}:00` });
        }
        const hourData = await Promise.all(
          hoursArr.map(({ day, hour }) =>
            fetch(`${url}hour/${day}_${hour}.json`).then(r => r.ok ? r.json() : {})
          )
        );
        labels = hoursArr.map(h => h.label);
        abTypeStatsArr = hourData.map(d => d.ab_type_stats || {});
        break;
      }
      case "day": {
        const days = Array.from({ length: 7 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - i);
          return date.toISOString().split('T')[0].replace(/-/g, '');
        }).reverse();
        const dayData = await Promise.all(
          days.map(day =>
            fetch(`${url}day/${day}.json`).then(r => r.ok ? r.json() : {})
          )
        );
        labels = days.map(day => {
          const date = new Date(`${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6)}`);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });
        abTypeStatsArr = dayData.map(d => d.ab_type_stats || {});
        break;
      }
      case "3days": {
        const possibleDays = [];
        const today = new Date();
        for (let i = 0; i < 60; i++) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          const dayStr = d.toISOString().split('T')[0].replace(/-/g, '');
          possibleDays.push(dayStr);
        }
        const dayData = await Promise.all(
          possibleDays.map(day =>
            fetch(`${url}3days/${day}.json`).then(r => r.ok ? r.json() : null)
          )
        );
        let filtered = possibleDays
          .map((day, i) => ({ day, data: dayData[i] }))
          .filter(item => item.data && Object.keys(item.data).length > 0);

        filtered = filtered.sort((a, b) => a.day.localeCompare(b.day));
        const lastFiltered = filtered.slice(-12);

        labels = lastFiltered.map(item => {
          const date = new Date(`${item.day.slice(0, 4)}-${item.day.slice(4, 6)}-${item.day.slice(6)}`);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });
        abTypeStatsArr = lastFiltered.map(item => item.data.ab_type_stats || {});
        break;
      }
      case "week": {
        const now = new Date();
        let weeks = [];
        let year = now.getUTCFullYear();
        let week = Math.ceil(
          ((Date.UTC(year, now.getUTCMonth(), now.getUTCDate()) -
            Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
        );
        for (let i = 0; i < 9; i++) {
          weeks.unshift({ year, week });
          week--;
          if (week === 0) {
            year--;
            const lastWeek = Math.ceil(
              ((Date.UTC(year, 11, 31) - Date.UTC(year, 0, 1)) / 86400000 + new Date(Date.UTC(year, 0, 1)).getUTCDay() + 1) / 7
            );
            week = lastWeek;
          }
        }
        const weekFiles = weeks.map(w => `${w.year}${String(w.week).padStart(2, "0")}`);
        const weekData = await Promise.all(
          weekFiles.map(file =>
            fetch(`${url}week/${file}.json`).then(r => r.ok ? r.json() : null)
          )
        );
        const filtered = weeks
          .map((w, i) => ({ w, data: weekData[i] }))
          .filter(item => item.data && Object.keys(item.data).length > 0);

        labels = filtered.map(item => `W${item.w.week} '${String(item.w.year).slice(2)}`);
        abTypeStatsArr = filtered.map(item => item.data.ab_type_stats || {});
        break;
      }
      case "month": {
        const now = new Date();
        let months = [];
        let year = now.getUTCFullYear();
        let month = now.getUTCMonth() + 1;
        for (let i = 0; i < 12; i++) {
          months.unshift({ year, month });
          month--;
          if (month === 0) {
            month = 12;
            year--;
          }
        }
        const monthFiles = months.map(m => `${m.year}${String(m.month).padStart(2, "0")}`);
        const monthData = await Promise.all(
          monthFiles.map(file =>
            fetch(`${url}month/${file}.json`).then(r => r.ok ? r.json() : {})
          )
        );
        labels = months.map(m => {
          const d = new Date(Date.UTC(m.year, m.month - 1, 1));
          return d.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
        });
        abTypeStatsArr = monthData.map(d => d.ab_type_stats || {});
        break;
      }
      case "half-year": {
        const now = new Date();
        let halfyears = [];
        let year = now.getUTCFullYear();
        let half = now.getUTCMonth() < 6 ? 1 : 2;
        for (let i = 0; i < 6; i++) {
          halfyears.unshift({ year, half });
          half--;
          if (half === 0) {
            half = 2;
            year--;
          }
        }
        const halfyearFiles = halfyears.map(hy => `${hy.year}_H${hy.half}`);
        const halfyearData = await Promise.all(
          halfyearFiles.map(file =>
            fetch(`${url}halfyear/${file}.json`).then(r => r.ok ? r.json() : {})
          )
        );
        labels = halfyears.map(hy => `${hy.year} H${hy.half}`);
        abTypeStatsArr = halfyearData.map(d => d.ab_type_stats || {});
        break;
      }
      case "year": {
        const years = Array.from({ length: 2 }, (_, i) => {
          const date = new Date();
          date.setFullYear(date.getFullYear() - i);
          return date.getFullYear().toString();
        }).reverse();
        const yearData = await Promise.all(
          years.map(year =>
            fetch(`${url}year/${year}.json`).then(r => r.ok ? r.json() : {})
          )
        );
        labels = years;
        abTypeStatsArr = yearData.map(d => d.ab_type_stats || {});
        break;
      }
      default: {
        setStats({ labels: [], datasets: [] });
        setLoading(false);
        return;
      }
    }

    setStats({
      labels,
      datasets: [
        {
          label: "Solo",
          data: abTypeStatsArr.map(stats => stats.A?.count ?? 0),
          backgroundColor: "#a259ff",
          borderRadius: 6,
          barPercentage: 0.8,
          categoryPercentage: 0.8,
          borderSkipped: false
        },
        {
          label: "Multi",
          data: abTypeStatsArr.map(stats => stats.B?.count ?? 0),
          backgroundColor: "#ff4ecd",
          borderRadius: 6,
          barPercentage: 0.8,
          categoryPercentage: 0.8,
          borderSkipped: false
        }
      ],
      rawAbTypeStatsArr: abTypeStatsArr
    });
  } catch (error) {
    setStats({ labels: [], datasets: [] });
  } finally {
    setLoading(false);
  }
}